import React, { useEffect, useReducer } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { BreadCrumb, FooterSection, FaqSection } from '../components';
import { MoreAboutRoute, StationTrainsSection, TrainBetweenStationsSection, ReturnTrainSection } from './components';
import { TrainClasses } from './TrainClasses';
import capitalise from '../../utils/capitalise';
import { formatDuration, formatDuration2 } from '../../utils/dateTimeFormatUtils';
import DataLoaderError from '../../errors/DataLoaderError';
import { RouteInfoData, WeeklyTrainInfo, EnvironmentVariables, RailwayStation } from '../types';
import { getAppEnv } from '../../../config/env';
import RouteInfoContext from './RouteInfoContext';
import { RECOMMEND_PATH } from '../components/RecommendTripPathList/utils/recommendTripPathListUtils';
import RecommendTripPathWrapper from '../components/RecommendTripPathList/RecommendTripPathList';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import SEARCH_WIDGET_UTILS from '../components/SearchWidget/utils/utils';
import HtmlHeader from '../components/HtmlHeader';
import AppDownloadBanner from '../components/AppDownloadBanner';
import { SEO_ROUTE_PAGE } from '../../utils/RailsSEOConstants';
import { getOtherDestFromCityInterlinkingBlocks, getOtherSrcToCityInterlinkingBlocks } from './RouteInfoUtils';
const { Provider: RouteInfoProvider } = RouteInfoContext;
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

function decorateListingData(listingData, templateId, routeInfo) {
  const {
    alternateRoutePageInterlinks,
    source,
    destination,
    topFlightRoutesFromCity,
    topFlightRoutesToCity,
    cabsRoutesFromCity,
    cabsRoutesToCity,
    busRoutesFromCity,
    busRoutesToCity,
    weeklyTrainList,
    weeklyCovidTrainList,
    weeklyFestiveTrainList,
    trainsStartingFromSource,
    trainsPassingThroughSource,
    otherDestinationsFromSource,
    otherSourcesToDestination,
    otherDestinationsFromDestination,
    otherSourcesToSource,
    topTrainRoutes,
    nearByCities,
    hotelsInSourceCity,
    hotelsInDestinationCity,
    template,
    alternatePageUrlInfo,
    faq,
    pageUrl,
    returnRoutePageUrl,
    moreInfoHeading,
    tokens,
    miscellaneous,
    returnRouteTitle,
    appDownloadTopStrip,
    isReturnRouteAvailable,
    ltsFromSource,
    ltsToDestination,
    stationsNearbySource,
    stationsNearbyDestination,
    busSeoRoutes,
    partialRouteImplementationConstants,
    popularTrainsFromSrcToDest,
    sourceWhere2GoInfo,
    destinationWhere2GoInfo
  } = listingData;

  const fromCity = tokens.source;
  const fromCityCode = routeInfo.fromCity.code;
  const toCity = tokens.destination;
  const toCityCode = routeInfo.toCity.code;

  const stationMap: Record<string, RailwayStation> = {};
  const trainsMap: Record<string, WeeklyTrainInfo> = {};
  const trainsBetweenStations: string[] = [];

  source.obj.railwayStations.forEach((station: RailwayStation) => {
    station.name = station.name.replace('Junction', 'Jn');
    station.name = station.name.replace('Railway Station', '');
    stationMap[station.code] = station;
  });

  destination.obj.railwayStations.forEach((station: RailwayStation) => {
    station.name = station.name.replace('Junction', '');
    stationMap[station.code] = station;
  });

  weeklyTrainList.list.forEach((train: WeeklyTrainInfo) => {
    train.durationFormatted = formatDuration(train.duration);
    train.durationParts = formatDuration2(train.duration);
    trainsMap[train.trainNumber] = train;
    trainsBetweenStations.push(train.trainNumber);
  });

  weeklyCovidTrainList.list.forEach((train: WeeklyTrainInfo) => {
    // if the current format is in format say 5hr, make it 5hr 0mins
    if (String(train.duration).endsWith('hr')) {
      train.duration = train.duration.concat(' 0mins');
    }
    train.durationFormatted = formatDuration(train.duration);
    train.durationParts = formatDuration2(train.duration.replace('hr ', ':').replace('mins', ''));
    return train;
  });

  weeklyFestiveTrainList.list.forEach((train: WeeklyTrainInfo) => {
    // if the current format is in format say 5hr, make it 5hr 0mins
    if (String(train.duration).endsWith('hr')) {
      train.duration = train.duration.concat(' 0mins');
    }
    train.durationFormatted = formatDuration(train.duration);
    train.durationParts = formatDuration2(train.duration.replace('hr ', ':').replace('mins', ''));
    return train;
  });
  const otherSrcToDest = getOtherSrcToCityInterlinkingBlocks(otherSourcesToDestination, toCity);
  const otherDestFromSrc = getOtherDestFromCityInterlinkingBlocks(otherDestinationsFromSource, fromCity);
  const otherDestFromDest = getOtherDestFromCityInterlinkingBlocks(otherDestinationsFromDestination, fromCity);
  const otherSrcToSrc = getOtherSrcToCityInterlinkingBlocks(otherSourcesToSource, toCity);

  return {
    alternateRoutePageInterlinks,
    source,
    destination,
    faq,
    template,
    fromCity,
    toCity,
    fromCityCode,
    toCityCode,
    stationMap,
    trainsBetweenStations,
    trainsMap,
    cabsRoutesFromCity,
    cabsRoutesToCity,
    busRoutesFromCity,
    busRoutesToCity,
    trainsFromSource: trainsStartingFromSource,
    trainsThroughSource: trainsPassingThroughSource,
    otherSrcToDest,
    otherDestFromSrc,
    otherDestFromDest,
    otherSrcToSrc,
    topTrainRoutes,
    nearByCities,
    hotelsInSourceCity,
    hotelsInDestinationCity,
    routeInfo,
    pageUrl,
    returnRoutePageUrl,
    topFlightRoutesFromCity,
    topFlightRoutesToCity,
    weeklyCovidTrainList,
    weeklyFestiveTrainList,
    weeklyTrainList,
    moreInfoHeading,
    tokens,
    miscellaneous,
    returnRouteTitle,
    templateId,
    alternatePageUrlInfo,
    appDownloadTopStrip,
    isReturnRouteAvailable,
    ltsFromSource,
    ltsToDestination,
    stationsNearbySource,
    stationsNearbyDestination,
    busSeoRoutes,
    partialRouteImplementationConstants,
    popularTrainsFromSrcToDest,
    sourceWhere2GoInfo,
    destinationWhere2GoInfo
  };
}

type PathParamsType = {
  fromCity: string;
  toCity: string;
};

type RouteInfoProps = RouteComponentProps<PathParamsType> & {
  data: RouteInfoData;
};

const RouteInfo = ({ data }: RouteInfoProps) => {
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);
  const { onTrainClicked, onTrainExtraSectionClicked, updateCityNameWithFilter, onMountInitSearchWidgetWrapper } =
    SEARCH_WIDGET_UTILS.ROUTE_INFO_UTILS(searchWidgetDispatch);
  const { fromFilter, toFilter } = searchWidgetState;
  const { source: vernacularSourceCityName, destination: vernacularDestinationCityName } = data.tokens;

  useEffect(() => {
    updateCityNameWithFilter({ fromFilter, toFilter, data });
  }, [fromFilter, toFilter]);

  return (
    <RouteInfoProvider
      value={{
        ...data,
        onTrainClicked: onTrainClicked,
        onTrainExtraSectionClicked: onTrainExtraSectionClicked
      }}
    >
      <div>
        <AppDownloadBanner {...data.template} />
        <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
          <SearchWidget
            openDatePickerOnMount={true}
            topFix={true}
            classNode={TrainClasses.FC}
            templateId={data.templateId}
            searchButtonTitle={data.miscellaneous['SELECT DATE AND CHECK AVAILABILITY']}
            onMountInitSearchWidget={onMountInitSearchWidgetWrapper(data)}
            pageWiseContext={SearchWidgetContext}
            pageName={SEO_ROUTE_PAGE}
          />
        </SearchWidgetContext.Provider>
        <div className="container">
          <HtmlHeader data={data} containsAlternateHref={true} />
          <BreadCrumb breadcrumb={data.template.breadcrumb} />
          <TrainBetweenStationsSection />
          {data.isReturnRouteAvailable && (
            <ReturnTrainSection
              fromCity={data.fromCity}
              toCity={data.toCity}
              title={data.returnRouteTitle
                .replace('${source}', vernacularSourceCityName)
                .replace('${destination}', vernacularDestinationCityName)}
              url={data.returnRoutePageUrl}
            />
          )}
          <StationTrainsSection trainListType={'originating'} />
          <StationTrainsSection trainListType={'passing'} />
          <FaqSection
            faq={data.faq.list}
            pageUrl={data.pageUrl}
            title={data.faq.title}
            bookNowString={data.miscellaneous['BOOK NOW']}
            pageName={SEO_ROUTE_PAGE}
          />
          <section style={{ flexWrap: 'wrap' }} className="railSeoWrap__stnDetails makeFlex">
            {<RecommendTripPathWrapper recommendPaths={RECOMMEND_PATH.ROUTE_INFO_PAGE(data)} />}
          </section>

          <section className="bottomItems">
            {data.template.extra_info && (
              <MoreAboutRoute
                fromCity={data.fromCity}
                toCity={data.toCity}
                extraInfo={data.template.extra_info}
                title={data.moreInfoHeading
                  .replace('${source}', vernacularSourceCityName)
                  .replace('${destination}', vernacularDestinationCityName)}
              />
            )}
          </section>
        </div>
        <FooterSection template={data.template} />
      </div>
    </RouteInfoProvider>
  );
};

RouteInfo.fetchData = async (_, match, urlObj) => {
  const {
    params: { fromCity, toCity, templateId }
  } = match;
  const [fromCityName, fromCityCode] = fromCity.split('-');
  const [toCityName, toCityCode] = toCity.split('-');
  const url = `${DATA_LOADER_URL}/api/routeTemplateData/${templateId}/${fromCityName}/${toCityName}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    throw new Error('JSON parse exception: ' + textResponse);
  }
  // eslint-disable-next-line no-console
  console.log('responseData.status', responseData.status);
  if (responseData.status !== 'SUCCESS') {
    throw new DataLoaderError(`Unable to fetch for ${fromCityName} ${toCityName}`, responseData.error);
  }
  try {
    return decorateListingData(responseData, templateId, {
      fromCity: {
        name: capitalise(fromCityName),
        code: fromCityCode.toUpperCase()
      },
      toCity: { name: capitalise(toCityName), code: toCityCode.toUpperCase() }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('err', e);
  }
};

export default RouteInfo;
